import { addWeight } from "actions/scales"; // Directly imported to avert circular import dependencies

export const DINI_3590_DRIVER_NAME = "dini-3590";

export enum Dini3590Unit {
  Kilograms = "kg",
  Tonnes = "t",
  Pounds = "lb",
  Grams = "g",
}

const GRAMS_PER_POUND = 453.592;

export interface Dini3590Config {
  "stable-code"?: string;
  "unstable-code"?: string;
  "zero-code"?: string;
  delimiter?: string;
  "stability-field-index"?: number;
  "weight-field-index"?: number;
  "unit-field-index"?: number;
}

interface Dini3590Device {
  deviceId: string;
  config: Dini3590Config;
}

export class Dini3590View {
  private fields: string[];
  private config: Dini3590Config;

  constructor(payload: string, config: Dini3590Config = {}) {
    const delimiter = config.delimiter || ",";
    this.fields = payload.split(delimiter);
    this.config = config;
  }

  get stabilityFieldIndex(): number {
    return this.config["stability-field-index"] ?? 0;
  }

  get weightFieldIndex(): number {
    return this.config["weight-field-index"] ?? 2;
  }

  get unitFieldIndex(): number {
    return this.config["unit-field-index"] ?? 3;
  }

  get stableCode(): string {
    return this.config["stable-code"] || "ST";
  }

  get unstableCode(): string {
    return this.config["unstable-code"] || "US";
  }

  get zeroCode(): string {
    return this.config["zero-code"] || "ZR";
  }

  get stabilityCode(): string {
    return (this.fields[this.stabilityFieldIndex] || "").trim();
  }

  get weightString(): string {
    return (this.fields[this.weightFieldIndex] || "").trim();
  }

  get unitString(): string {
    return (this.fields[this.unitFieldIndex] || "").trim();
  }

  isValid(): boolean {
    const maxIndex = Math.max(
      this.stabilityFieldIndex,
      this.weightFieldIndex,
      this.unitFieldIndex,
    );
    const status = this.stabilityCode;
    // Weight and unit are validated strictly to reject packets corrupted by serial
    // line bit-flips. e.g. "3500" → "3p00" causes parseFloat to silently return 3,
    // and a garbled unit like "kç" would otherwise fall through to a kg default.
    return (
      this.fields.length > maxIndex &&
      (status === this.stableCode ||
        status === this.unstableCode ||
        status === this.zeroCode) &&
      /^-?\d+(\.\d+)?$/.test(this.weightString) &&
      Object.values(Dini3590Unit).includes(this.unitString as Dini3590Unit)
    );
  }

  isStable(): boolean {
    return (
      this.stabilityCode === this.stableCode ||
      this.stabilityCode === this.zeroCode
    );
  }

  displayedWeightGrams(): number {
    const weight = parseFloat(this.weightString);

    switch (this.unitString) {
      case Dini3590Unit.Tonnes:
        return weight * 1_000_000;
      case Dini3590Unit.Pounds:
        return weight * GRAMS_PER_POUND;
      case Dini3590Unit.Grams:
        return weight;
      case Dini3590Unit.Kilograms:
        return weight * 1000;
    }
  }
}

export function dini3590ProcessMessage(
  device: Dini3590Device,
  _type: unknown,
  _status: unknown,
  serialisedPayload: string,
) {
  const { deviceId, config } = device;

  // Decode base64 to ASCII string and strip CR/LF terminators
  const rawString = atob(serialisedPayload);
  const payload = rawString.replace(/[\r\n]+$/, "");

  const view = new Dini3590View(payload, config);

  if (!view.isValid()) {
    return;
  }

  return addWeight(
    deviceId,
    new Date(),
    view.displayedWeightGrams(),
    view.isStable(),
  );
}
